/**
 * Analytics module — centralizes all gtag event tracking.
 * No financial input data is ever sent.
 */

const APP_VERSION = '2.0.0';
function gtag(event: string, params?: Record<string, any>) {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', event, { ...params, app_version: APP_VERSION });
  }
}

// ─── Engagement Events ───

export function trackCalculation(scenario: string) {
  gtag('calculate_runway', { event_category: 'engagement', event_label: scenario, value: 1 });
}

export function trackScenarioToggle(scenario: string) {
  gtag('scenario_toggle', { event_category: 'engagement', event_label: scenario });
}

export function trackRiskZoneShown(riskLabel: 'Critical' | 'Vulnerable' | 'Stable') {
  gtag('risk_zone_shown', { event_category: 'engagement', event_label: riskLabel });
}

export function trackImproveSafetyInteraction(action?: string) {
  gtag('improve_safety_interaction', { event_category: 'engagement', event_label: action });
}

export function trackPageView(page?: string) {
  gtag('page_view', { event_category: 'engagement', event_label: page });
}

export function trackGraphView() {
  gtag('graph_view', { event_category: 'engagement', event_label: 'projection_chart' });
}

export function trackResultsViewDuration(seconds: number) {
  gtag('results_view_duration', { event_category: 'engagement', event_label: 'Time on Results', value: seconds });
}

export function trackRepeatVisit() {
  gtag('repeat_visit', { event_category: 'engagement', event_label: 'returning_user' });
}

export function trackSessionEngagement(seconds: number) {
  gtag('session_engagement', { event_category: 'engagement', value: seconds });
}

// ─── Feedback Events ───

export function trackFeedbackHelpful(value: 'yes' | 'somewhat' | 'not-really') {
  gtag('feedback_emotion_selected', { event_category: 'feedback', event_label: value });
}

export function trackBehaviorChanged(value: 'yes' | 'no') {
  gtag(value === 'yes' ? 'behavior_changed_yes' : 'behavior_changed_no', { event_category: 'feedback' });
}

export function trackFeedbackText(text: string) {
  gtag('feedback_text_submitted', { event_category: 'feedback', feedback_text: text });
}

// ─── V1.1 Reflection Events ───

export function trackReflection(value: 'yes' | 'no' | 'rethink') {
  const eventMap = {
    'yes': 'reflection_surprised_yes',
    'no': 'reflection_surprised_no',
    'rethink': 'reflection_rethink',
  };
  gtag(eventMap[value], { event_category: 'reflection' });
}

// ─── User Journey Click Events ───

export function trackAccordionToggle(section: string, isOpen: boolean) {
  gtag('accordion_toggle', {
    event_category: 'user_journey',
    event_label: section,
    value: isOpen ? 1 : 0,
  });
}

export function trackWizardStepClick(step: number, method: 'next' | 'back' | 'direct') {
  gtag('wizard_step_navigation', {
    event_category: 'user_journey',
    event_label: `step_${step}_${method}`,
    value: step,
  });
}

export function trackThemeToggle(theme: 'light' | 'dark') {
  gtag('theme_toggle', {
    event_category: 'user_journey',
    event_label: theme,
  });
}

export function trackResetClick() {
  gtag('reset_click', {
    event_category: 'user_journey',
    event_label: 'reset_all_inputs',
  });
}

export function trackClearDataClick() {
  gtag('clear_data_click', {
    event_category: 'user_journey',
    event_label: 'clear_saved_data',
  });
}

export function trackCalculateClick() {
  gtag('calculate_click', {
    event_category: 'user_journey',
    event_label: 'calculate_runway_cta',
  });
}

export function trackExportPDFClick() {
  gtag('export_pdf_click', {
    event_category: 'user_journey',
    event_label: 'export_pdf_disabled',
  });
}

export function trackSaveScenarioClick() {
  gtag('save_scenario_click', {
    event_category: 'user_journey',
    event_label: 'save_scenario_cta',
  });
}

export function trackNavDropdownToggle(label: string, isOpen: boolean) {
  gtag('nav_dropdown_toggle', {
    event_category: 'navigation',
    event_label: label,
    value: isOpen ? 1 : 0,
  });
}

export function trackNavDropdownSelection(label: string, item: string) {
  gtag('nav_dropdown_selection', {
    event_category: 'navigation',
    event_label: `${label}:${item}`,
  });
}

export function trackNavLinkClick(label: string) {
  gtag('nav_link_click', {
    event_category: 'navigation',
    event_label: label,
  });
}

export function trackNavMenuToggle(isOpen: boolean) {
  gtag('nav_menu_toggle', {
    event_category: 'navigation',
    event_label: 'mobile_menu',
    value: isOpen ? 1 : 0,
  });
}
